import React, { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Sparkles,
  Check,
  X,
  Edit3,
  Loader2,
  Clock,
  TrendingUp,
  AlertCircle,
  ChevronDown,
  ChevronUp,
  Zap,
  DollarSign,
} from "lucide-react";
import { timeEntriesApi } from "../../services/api";

// ─── Types ──────────────────────────────────────────────────────────────────

interface Suggestion {
  _id: string;
  matterId: {
    _id: string;
    matterNumber?: string;
    title?: string;
  } | null;
  clientId: {
    _id: string;
    displayName?: string;
  } | null;
  narrative: string;
  minutes: number;
  amountCents: number;
  billable: boolean;
  aiConfidence: number;
  date: string;
  status: string;
  activityLogIds?: string[];
}

interface Props {
  onDataChange?: () => void;
}

// ─── Helpers ────────────────────────────────────────────────────────────────

const formatCurrency = (cents: number) =>
  new Intl.NumberFormat("en-CA", { style: "currency", currency: "CAD" }).format(cents / 100);

const formatHours = (minutes: number) => {
  const h = (minutes / 60).toFixed(1);
  return `${h} hrs`;
};

const formatMinutes = (mins: number) => {
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
};

function confidenceConfig(value: number) {
  if (value >= 0.8)
    return { label: `${Math.round(value * 100)}%`, colorClass: "bg-emerald-100 text-emerald-700 border-emerald-200", dotColor: "bg-emerald-500" };
  if (value >= 0.5)
    return { label: `${Math.round(value * 100)}%`, colorClass: "bg-amber-100 text-amber-700 border-amber-200", dotColor: "bg-amber-500" };
  return { label: `${Math.round(value * 100)}%`, colorClass: "bg-red-50 text-red-600 border-red-200", dotColor: "bg-red-500" };
}

const MATTER_COLORS = [
  "#0F5132", "#1976D2", "#7B1FA2", "#C62828", "#EF6C00",
  "#00838F", "#4527A0", "#2E7D32", "#AD1457", "#283593",
];

function matterColor(matterId: string): string {
  let hash = 0;
  for (let i = 0; i < matterId.length; i++) {
    hash = (hash << 5) - hash + matterId.charCodeAt(i);
  }
  return MATTER_COLORS[Math.abs(hash) % MATTER_COLORS.length];
}

type Period = "24h" | "3d" | "7d";

function periodToDateRange(period: Period): { from: string; to: string } {
  const to = new Date();
  const from = new Date();
  if (period === "24h") from.setDate(from.getDate() - 1);
  else if (period === "3d") from.setDate(from.getDate() - 3);
  else from.setDate(from.getDate() - 7);
  return { from: from.toISOString(), to: to.toISOString() };
}

// ─── Component ──────────────────────────────────────────────────────────────

const AiTimeSuggestionsPanel: React.FC<Props> = ({ onDataChange }) => {
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [period, setPeriod] = useState<Period>("3d");
  const [collapsed, setCollapsed] = useState(false);

  // Edit state
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editNarrative, setEditNarrative] = useState("");
  const [editMinutes, setEditMinutes] = useState(0);

  // Action loading states
  const [approvingId, setApprovingId] = useState<string | null>(null);
  const [dismissingId, setDismissingId] = useState<string | null>(null);
  const [bulkApproving, setBulkApproving] = useState(false);

  const fetchSuggestions = useCallback(async () => {
    try {
      setLoading(true);
      const res = await timeEntriesApi.getSuggestions();
      const data = res.data?.data ?? res.data ?? [];
      setSuggestions(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Failed to load AI suggestions:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSuggestions();
  }, [fetchSuggestions]);

  const handleGenerate = async () => {
    try {
      setGenerating(true);
      const range = periodToDateRange(period);
      await timeEntriesApi.generateSuggestions(range);
      await fetchSuggestions();
    } catch (err) {
      console.error("Failed to generate suggestions:", err);
    } finally {
      setGenerating(false);
    }
  };

  const handleApprove = async (s: Suggestion) => {
    try {
      setApprovingId(s._id);
      const data: Record<string, any> = { narrative: s.narrative, minutes: s.minutes };
      if (editingId === s._id) {
        data.narrative = editNarrative;
        data.minutes = editMinutes;
      }
      await timeEntriesApi.approveSuggestion(s._id, data);
      setSuggestions((prev) => prev.filter((x) => x._id !== s._id));
      setEditingId(null);
      onDataChange?.();
    } catch (err) {
      console.error("Failed to approve suggestion:", err);
    } finally {
      setApprovingId(null);
    }
  };

  const handleDismiss = async (id: string) => {
    try {
      setDismissingId(id);
      await timeEntriesApi.dismissSuggestion(id);
      setSuggestions((prev) => prev.filter((x) => x._id !== id));
    } catch (err) {
      console.error("Failed to dismiss suggestion:", err);
    } finally {
      setDismissingId(null);
    }
  };

  const handleBulkApprove = async () => {
    try {
      setBulkApproving(true);
      const ids = suggestions.map((s) => s._id);
      await timeEntriesApi.bulkApproveSuggestions(ids);
      setSuggestions([]);
      onDataChange?.();
    } catch (err) {
      console.error("Failed to bulk approve:", err);
    } finally {
      setBulkApproving(false);
    }
  };

  const totalMinutes = suggestions.reduce((sum, s) => sum + s.minutes, 0);
  const totalCents = suggestions.reduce((sum, s) => sum + s.amountCents, 0);

  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
      {/* Gradient header */}
      <div
        className="px-5 py-4"
        style={{
          background: "linear-gradient(135deg, #0F5132 0%, #198754 50%, #20c997 100%)",
        }}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="text-white font-semibold text-sm flex items-center gap-2">
                AI Time Assistant
                {suggestions.length > 0 && (
                  <span className="text-[10px] font-bold bg-white/25 text-white px-1.5 py-0.5 rounded-full">
                    {suggestions.length}
                  </span>
                )}
              </h3>
              <p className="text-emerald-100 text-xs mt-0.5">
                Suggest entries from your recent activity
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* Period pills */}
            <div className="flex items-center bg-white/15 rounded-lg p-0.5">
              {(["24h", "3d", "7d"] as Period[]).map((p) => (
                <button
                  key={p}
                  onClick={() => setPeriod(p)}
                  className={`px-2.5 py-1 rounded-md text-xs font-medium transition-all ${
                    period === p
                      ? "bg-white text-emerald-800 shadow-sm"
                      : "text-white/80 hover:text-white hover:bg-white/10"
                  }`}
                >
                  {p === "24h" ? "24h" : p === "3d" ? "3 days" : "7 days"}
                </button>
              ))}
            </div>
            <button
              onClick={handleGenerate}
              disabled={generating}
              className="flex items-center gap-1.5 px-3.5 py-2 bg-white text-emerald-800 font-semibold text-xs rounded-lg hover:bg-emerald-50 transition-colors disabled:opacity-60"
            >
              {generating ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <Zap className="w-3.5 h-3.5" />
              )}
              {generating ? "Analyzing..." : "Generate"}
            </button>
            <button
              onClick={() => setCollapsed(!collapsed)}
              className="p-1.5 rounded-lg text-white/70 hover:text-white hover:bg-white/10 transition-colors"
            >
              {collapsed ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />}
            </button>
          </div>
        </div>
      </div>

      {/* Content area */}
      <AnimatePresence>
        {!collapsed && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25 }}
          >
            {/* Loading skeleton */}
            {generating && suggestions.length === 0 && (
              <div className="p-5 space-y-3">
                <div className="flex items-center gap-2 text-sm text-slate-500 mb-4">
                  <Loader2 className="w-4 h-4 animate-spin text-emerald-600" />
                  Analyzing your activity...
                </div>
                {[1, 2, 3].map((i) => (
                  <div key={i} className="animate-pulse flex gap-3">
                    <div className="w-1 rounded-full bg-slate-200" />
                    <div className="flex-1 space-y-2 py-2">
                      <div className="h-4 bg-slate-200 rounded w-1/3" />
                      <div className="h-3 bg-slate-100 rounded w-2/3" />
                      <div className="h-3 bg-slate-100 rounded w-1/4" />
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Loading initial */}
            {loading && (
              <div className="flex items-center justify-center py-10">
                <Loader2 className="w-5 h-5 animate-spin text-emerald-600" />
                <span className="ml-2 text-sm text-slate-500">Loading suggestions...</span>
              </div>
            )}

            {/* Empty state */}
            {!loading && !generating && suggestions.length === 0 && (
              <div className="flex flex-col items-center justify-center py-10 px-6">
                <div className="w-14 h-14 rounded-2xl bg-slate-100 flex items-center justify-center mb-3">
                  <Sparkles className="w-7 h-7 text-slate-300" />
                </div>
                <p className="text-sm font-medium text-slate-700 mb-1">No suggestions available</p>
                <p className="text-xs text-slate-400 text-center max-w-xs">
                  Your recent activity will appear here for AI-powered time entry creation. Click Generate to analyze your work.
                </p>
              </div>
            )}

            {/* Suggestion cards */}
            {!loading && suggestions.length > 0 && (
              <div className="p-4 space-y-3">
                <AnimatePresence mode="popLayout">
                  {suggestions.map((s, idx) => {
                    const conf = confidenceConfig(s.aiConfidence ?? 0.5);
                    const isEditing = editingId === s._id;
                    const color = s.matterId?._id ? matterColor(s.matterId._id) : "#94a3b8";

                    return (
                      <motion.div
                        key={s._id}
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, x: -40, height: 0 }}
                        transition={{ duration: 0.25, delay: idx * 0.04 }}
                        layout
                        className="flex gap-3 group"
                      >
                        {/* Color indicator bar */}
                        <div
                          className="w-1 rounded-full flex-shrink-0"
                          style={{ backgroundColor: color }}
                        />

                        <div className="flex-1 bg-slate-50 hover:bg-white border border-slate-200 rounded-xl p-4 transition-colors">
                          {/* Top row */}
                          <div className="flex items-start justify-between mb-2">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <p className="text-sm font-semibold text-slate-800 truncate">
                                  {s.matterId?.matterNumber && (
                                    <span className="text-slate-400 mr-1">{s.matterId.matterNumber}</span>
                                  )}
                                  {s.matterId?.title ?? "Unknown Matter"}
                                </p>
                                <span className="flex-shrink-0 inline-flex items-center gap-1 text-[10px] font-medium text-emerald-600 bg-emerald-50 border border-emerald-100 px-1.5 py-0.5 rounded-full">
                                  <Sparkles className="w-2.5 h-2.5" />
                                  AI Generated
                                </span>
                              </div>
                              {s.clientId?.displayName && (
                                <p className="text-xs text-slate-400 mt-0.5">{s.clientId.displayName}</p>
                              )}
                            </div>
                            {/* Confidence */}
                            <span className={`ml-2 flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full border flex-shrink-0 ${conf.colorClass}`}>
                              <span className={`w-1.5 h-1.5 rounded-full ${conf.dotColor}`} />
                              {conf.label}
                            </span>
                          </div>

                          {/* Narrative (view or edit) */}
                          {isEditing ? (
                            <div className="space-y-2 mb-3">
                              <textarea
                                value={editNarrative}
                                onChange={(e) => setEditNarrative(e.target.value)}
                                rows={2}
                                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:border-emerald-500 resize-none"
                              />
                              <div className="flex items-center gap-2">
                                <label className="text-xs text-slate-500">Minutes:</label>
                                <input
                                  type="number"
                                  min={1}
                                  value={editMinutes}
                                  onChange={(e) => setEditMinutes(Math.max(1, parseInt(e.target.value) || 1))}
                                  className="w-20 rounded-lg border border-slate-200 px-2 py-1.5 text-sm focus:outline-none focus:border-emerald-500"
                                />
                              </div>
                            </div>
                          ) : (
                            <p className="text-sm text-slate-600 leading-relaxed mb-3">{s.narrative}</p>
                          )}

                          {/* Bottom row: meta + actions */}
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <span className="inline-flex items-center gap-1 text-xs font-medium text-slate-600 bg-white border border-slate-200 px-2 py-1 rounded-lg">
                                <Clock className="w-3 h-3 text-slate-400" />
                                {formatMinutes(isEditing ? editMinutes : s.minutes)}
                              </span>
                              <span className="inline-flex items-center gap-1 text-xs font-medium text-slate-600 bg-white border border-slate-200 px-2 py-1 rounded-lg">
                                <DollarSign className="w-3 h-3 text-slate-400" />
                                {formatCurrency(s.amountCents)}
                              </span>
                              {!s.billable && (
                                <span className="text-[10px] font-medium text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded-full">
                                  Non-billable
                                </span>
                              )}
                            </div>

                            <div className="flex items-center gap-1.5">
                              {isEditing ? (
                                <>
                                  <button
                                    onClick={() => setEditingId(null)}
                                    className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
                                    title="Cancel"
                                  >
                                    <X className="w-4 h-4" />
                                  </button>
                                  <button
                                    onClick={() => handleApprove(s)}
                                    disabled={approvingId === s._id}
                                    className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-white bg-emerald-600 hover:bg-emerald-700 rounded-lg transition-colors disabled:opacity-50"
                                  >
                                    {approvingId === s._id ? (
                                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                    ) : (
                                      <Check className="w-3.5 h-3.5" />
                                    )}
                                    Save
                                  </button>
                                </>
                              ) : (
                                <>
                                  <button
                                    onClick={() => handleDismiss(s._id)}
                                    disabled={dismissingId === s._id}
                                    className="p-1.5 rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-50 transition-colors disabled:opacity-50"
                                    title="Dismiss"
                                  >
                                    {dismissingId === s._id ? (
                                      <Loader2 className="w-4 h-4 animate-spin" />
                                    ) : (
                                      <X className="w-4 h-4" />
                                    )}
                                  </button>
                                  <button
                                    onClick={() => {
                                      setEditingId(s._id);
                                      setEditNarrative(s.narrative);
                                      setEditMinutes(s.minutes);
                                    }}
                                    className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
                                    title="Edit"
                                  >
                                    <Edit3 className="w-4 h-4" />
                                  </button>
                                  <button
                                    onClick={() => handleApprove(s)}
                                    disabled={approvingId === s._id}
                                    className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-white bg-emerald-600 hover:bg-emerald-700 rounded-lg transition-colors disabled:opacity-50"
                                    title="Accept"
                                  >
                                    {approvingId === s._id ? (
                                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                    ) : (
                                      <Check className="w-3.5 h-3.5" />
                                    )}
                                    Accept
                                  </button>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    );
                  })}
                </AnimatePresence>

                {/* Bottom actions */}
                {suggestions.length > 1 && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="flex items-center justify-between pt-3 border-t border-slate-100"
                  >
                    <p className="text-xs text-slate-500">
                      <span className="font-semibold text-slate-700">{suggestions.length}</span> suggestions
                      {" \u00B7 "}
                      {formatHours(totalMinutes)}
                      {" \u00B7 "}
                      {formatCurrency(totalCents)}
                    </p>
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => {
                          suggestions.forEach((s) => handleDismiss(s._id));
                        }}
                        className="text-xs text-slate-400 hover:text-red-500 transition-colors"
                      >
                        Dismiss All
                      </button>
                      <button
                        onClick={handleBulkApprove}
                        disabled={bulkApproving}
                        className="flex items-center gap-1.5 px-3.5 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold rounded-lg transition-colors disabled:opacity-50"
                      >
                        {bulkApproving ? (
                          <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        ) : (
                          <Check className="w-3.5 h-3.5" />
                        )}
                        Accept All
                      </button>
                    </div>
                  </motion.div>
                )}
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AiTimeSuggestionsPanel;
